<template>
  <b-modal
    v-model="editorShown"
    :title="contact ? 'Edit Contact' : 'Add Contact'"
    @ok="submitContact"
    centered
    no-close-on-backdrop
    header-bg-variant="primary"
    header-text-variant="white"
    header-class="bg-purple"
  >
    <template v-slot:modal-footer="{ ok, cancel }">
      <b-button variant="secondary" @click="cancel()">
        Cancel
      </b-button>
      <b-button variant="primary" @click="ok()">
        Save
      </b-button>
    </template>
    <b-row>
      <b-col>
        <b-form-group
          label="Name*"
          maxLength="100"
          :state="formErrors.name.length == 0 ? null : false"
          :invalid-feedback="formErrors.name.join(', ')"
        >
          <template v-slot:label>
            Name <span class="text-danger">*</span>
          </template>
          <b-form-input
            v-model="form.name"
            trim
            :state="formErrors.name.length == 0 ? null : false"
          ></b-form-input>
        </b-form-group>
      </b-col>
    </b-row>
    <b-form-group label="Relationship">
      <v-select v-model="form.relationship" :options="relationships"></v-select>
    </b-form-group>
    <b-form-group v-if="isOtherRelationSelected">
      <b-form-input
        placeholder="Other"
        v-model="form.relationshipOther"
        trim
        maxLength="100"
      >
      </b-form-input>
    </b-form-group>
    <b-form-group
      label="Email"
      :state="formErrors.email.length == 0 ? null : false"
      :invalid-feedback="formErrors.email.join(', ')"
    >
      <b-form-input
        v-model="form.email"
        trim
        maxLength="200"
        :state="formErrors.email.length == 0 ? null : false"
      ></b-form-input>
    </b-form-group>
    <b-form-group
      label="Mobile Phone #"
      maxLength="30"
      :state="formErrors.phone.length == 0 ? null : false"
      :invalid-feedback="formErrors.phone.join(', ')"
    >
      <phone-input
        ref="phoneInput"
        defaultCountry="US"
        v-model="form.phone"
        trim
        :state="formErrors.phone.length == 0 ? null : false"
      ></phone-input>
    </b-form-group>
    <b-form-group label="Street address">
      <b-form-input v-model="form.address" trim maxLength="100"></b-form-input>
    </b-form-group>
    <b-form-group label="City">
      <b-form-input v-model="form.city" trim maxLength="100"></b-form-input>
    </b-form-group>
    <b-row>
      <b-col>
        <b-form-group
          label="Zip Code"
          :state="formErrors.zipCode.length == 0 ? null : false"
          :invalid-feedback="formErrors.zipCode.join(', ')"
        >
          <b-form-input
            v-model="form.zipCode"
            v-facade="'#####'"
            trim
            :state="formErrors.zipCode.length == 0 ? null : false"
          ></b-form-input>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group label="State">
          <v-select v-model="form.state" :options="states"></v-select>
        </b-form-group>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import { mapState } from "vuex";
import {
  validateEmail,
  validatePhoneNumber,
  validateRequired,
  validateZipcode
} from "../../helpers/validation";
import PhoneInput from "../controls/PhoneInput.vue";
export default {
  name: "PatientContactForm",
  components: { PhoneInput },
  props: ["contact", "patientEmail", "contacts"],
  data() {
    return {
      form: {
        name: "",
        email: "",
        phone: "",
        address: "",
        zipCode: "",
        city: "",
        relationship: null,
        state: null,
        relationshipOther: null
      },
      formErrors: {
        name: [],
        email: [],
        phone: [],
        zipCode: []
      }
    };
  },
  mounted() {
    if (this.contact) {
      this.form = {
        ...this.contact,
        relationship: this.contact.relationship
          ? {
              value: this.contact.relationship.id,
              label: this.contact.relationship.name
            }
          : null,
        relationshipOther: this.contact.relationshipOther,
        state: this.contact.state
          ? {
              value: this.contact.state.id,
              label: this.contact.state.name
            }
          : null
      };
    }
  },
  methods: {
    validateContactSubmit() {
      let withoutErrors = true;

      this.formErrors.name = [];
      if (!validateRequired(this.form.name)) {
        this.formErrors.name.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.email = [];
      if (this.form.email && !validateEmail(this.form.email)) {
        this.formErrors.email.push("Invalid email");
        withoutErrors = false;
      }

      if (this.form.email && this.patientEmail == this.form.email) {
        this.formErrors.email.push(
          "This email address is being used by the patient"
        );
        withoutErrors = false;
      }

      const contactEmails = this.contacts.map(contact => contact.email);
      if (
        this.form.email &&
        contactEmails.indexOf(this.form.email) >= 0 &&
        (!this.contact || this.contact.email != this.form.email)
      ) {
        this.formErrors.email.push(
          "This email is being used by one of the patient's contact"
        );
        withoutErrors = false;
      }

      this.formErrors.phone = [];
      if (
        this.form.phone.trim().length >
          this.activeCountry.dialCode.length + 1 &&
        !validatePhoneNumber(this.form.phone)
      ) {
        this.formErrors.phone.push("Invalid phone number");
        withoutErrors = false;
      }

      this.formErrors.zipCode = [];
      if (this.form.zipCode && !validateZipcode(this.form.zipCode)) {
        this.formErrors.zipCode.push(
          "This field must have a valid zipcode format"
        );
        withoutErrors = false;
      }

      return withoutErrors;
    },
    async submitContact(modalEvent) {
      modalEvent.preventDefault();
      const result = this.validateContactSubmit();
      if (result) {
        const contactData = {
          ...this.form,
          phone:
            this.form.phone.trim().length >
            this.activeCountry.dialCode.length + 1
              ? this.form.phone
              : "",
          relationship: this.form.relationship
            ? {
                id: this.form.relationship.value,
                name: this.form.relationship.label
              }
            : null,
          relationshipOther: !this.isOtherRelationSelected
            ? null
            : this.form.relationshipOther,
          state: this.form.state
            ? {
                id: this.form.state.value,
                name: this.form.state.label
              }
            : null
        };
        this.$emit("ok", contactData);
      }
    }
  },
  computed: {
    ...mapState("client", {
      states: state =>
        state.states.map(state => ({
          value: state.id,
          label: state.name
        })),
      relationships: state =>
        state.relationships.map(relationship => ({
          value: relationship.id,
          label: relationship.name
        }))
    }),
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    isOtherRelationSelected: {
      get() {
        return this.form.relationship
          ? this.form.relationship.label === "Other"
          : false;
      }
    },
    activeCountry() {
      return this.$refs.phoneInput.$refs.telInput.activeCountry;
    }
  }
};
</script>
